/**
 * SurebetViewMarketFilter - Multi-select by margin keys in surebet.margins.
 * Options from current surebets payload (+ persisted selections).
 */

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSurebet } from '../context/SurebetContext';
import { useTranslation } from '../../i18n/hooks';
import { getMarginTypeName } from '../utils/surebetUtils';
import { collectMarketKeys } from '../viewFilters/surebetViewFilters';
import '../../bestOdds/components/BestOddsBookmakerFilter.css';

const SurebetViewMarketFilter = ({ className = '' }) => {
  const { t, language } = useTranslation();
  const {
    surebets,
    viewFilters,
    setSelectedMarketKeys
  } = useSurebet();

  const availableMarkets = useMemo(() => {
    const keys = new Set(collectMarketKeys(surebets));

    if (Array.isArray(viewFilters?.selectedMarketKeys)) {
      viewFilters.selectedMarketKeys.forEach((key) => {
        if (key && String(key).trim()) {
          keys.add(String(key));
        }
      });
    }

    return Array.from(keys)
      .map((key) => ({
        key,
        label: getMarginTypeName(key, language)
      }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [surebets, viewFilters?.selectedMarketKeys, language]);

  const availableKeys = useMemo(
    () => availableMarkets.map((item) => item.key),
    [availableMarkets]
  );

  const selectedMarketKeys = viewFilters?.selectedMarketKeys;
  const isAllSelected = selectedMarketKeys === null;

  const selectedCount = isAllSelected
    ? availableKeys.length
    : (Array.isArray(selectedMarketKeys) ? selectedMarketKeys.length : 0);

  const isMarketSelected = useCallback((marketKey) => {
    if (selectedMarketKeys === null || !Array.isArray(selectedMarketKeys)) {
      return true;
    }
    return selectedMarketKeys.includes(marketKey);
  }, [selectedMarketKeys]);

  const handleSelectAll = useCallback(() => {
    setSelectedMarketKeys(null);
  }, [setSelectedMarketKeys]);

  const handleDeselectAll = useCallback(() => {
    setSelectedMarketKeys([]);
  }, [setSelectedMarketKeys]);

  const handleToggle = useCallback((marketKey) => {
    if (selectedMarketKeys === null || !Array.isArray(selectedMarketKeys)) {
      setSelectedMarketKeys(availableKeys.filter((key) => key !== marketKey));
      return;
    }

    if (selectedMarketKeys.includes(marketKey)) {
      setSelectedMarketKeys(selectedMarketKeys.filter((key) => key !== marketKey));
      return;
    }

    const next = [...selectedMarketKeys, marketKey];
    const allSelected = availableKeys.length > 0
      && availableKeys.every((key) => next.includes(key));

    setSelectedMarketKeys(allSelected ? null : next);
  }, [availableKeys, selectedMarketKeys, setSelectedMarketKeys]);

  if (availableMarkets.length === 0) {
    return null;
  }

  return (
    <div
      className={`best-odds-bookmaker-filter best-odds-bookmaker-filter--wide ${className}`.trim()}
      role="group"
      aria-label={t('surebets.viewFilters.marketsAriaLabel')}
    >
      <div className="best-odds-bookmaker-filter__header">
        <span className="best-odds-bookmaker-filter__label">
          {t('surebets.viewFilters.marketsLabel')}
          <span className="best-odds-bookmaker-filter__count">
            ({selectedCount}/{availableKeys.length})
          </span>
        </span>
        <div className="best-odds-bookmaker-filter__actions">
          <button
            type="button"
            className="best-odds-bookmaker-filter__action-btn"
            onClick={handleSelectAll}
            disabled={isAllSelected}
          >
            {t('surebets.viewFilters.marketsSelectAll')}
          </button>
          <button
            type="button"
            className="best-odds-bookmaker-filter__action-btn"
            onClick={handleDeselectAll}
            disabled={Array.isArray(selectedMarketKeys) && selectedMarketKeys.length === 0}
          >
            {t('surebets.viewFilters.marketsDeselectAll')}
          </button>
        </div>
      </div>

      <div className="best-odds-bookmaker-filter__grid">
        {availableMarkets.map((market) => {
          const checked = isMarketSelected(market.key);
          return (
            <label
              key={market.key}
              className={`best-odds-bookmaker-filter__item${checked ? ' is-selected' : ''}`}
            >
              <input
                type="checkbox"
                className="best-odds-bookmaker-filter__checkbox"
                checked={checked}
                onChange={() => handleToggle(market.key)}
                value={market.key}
              />
              <span className="best-odds-bookmaker-filter__name" title={market.label}>
                {market.label}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

SurebetViewMarketFilter.propTypes = {
  className: PropTypes.string
};

export default SurebetViewMarketFilter;
